// src/hooks/useDelayedEffect.js
import { useEffect, useState } from 'react';

/**
 * Hook for creating sequentially delayed effects
 * @param {Array} steps - Array of delays in ms for each step
 * @param {boolean} autoStart - Whether to start the sequence automatically
 * @returns {Object} Current step and control functions
 */
export const useDelayedEffect = (steps = [1000], autoStart = true) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isRunning, setIsRunning] = useState(autoStart);
  
  useEffect(() => {
    if (!isRunning) return;
    
    // If we've reached the end, stop
    if (currentStep >= steps.length - 1) {
      setIsRunning(false);
      return;
    }
    
    // Move to the next step after the current delay
    const nextStep = currentStep + 1;
    const delay = steps[nextStep] || 1000;
    
    const timerId = setTimeout(() => {
      setCurrentStep(nextStep);
    }, delay);
    
    return () => clearTimeout(timerId);
  }, [currentStep, isRunning, steps]);
  
  const start = () => {
    setCurrentStep(-1);
    setIsRunning(true);
  };
  
  const stop = () => {
    setIsRunning(false);
  };
  
  const reset = () => {
    setCurrentStep(-1);
  };
  
  return {
    currentStep,
    isComplete: currentStep === steps.length - 1,
    isRunning,
    start,
    stop,
    reset
  };
};